<template>
  <div class="chat-page flex flex-col bg-theme-bg2 overflow-hidden h-full">
    <div class="flex-1 flex overflow-hidden max-w-7xl mx-auto w-full">

      <!-- ═══════════ SIDEBAR ═══════════ -->
      <aside
        class="sidebar flex flex-col bg-theme-card border-r border-theme-border"
        :class="{ 'sidebar-hidden': activeChat }"
      >
        <!-- Sidebar header -->
        <div class="px-4 pt-4 pb-3 shrink-0">
          <div class="flex items-center justify-between mb-3">
            <h1 class="text-lg font-bold text-theme-text tracking-tight">Messages</h1>
            <span
              v-if="totalUnread > 0"
              class="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 bg-primary-600 text-white text-[11px] font-bold rounded-full"
            >
              {{ totalUnread > 99 ? '99+' : totalUnread }}
            </span>
          </div>
          <!-- Search -->
          <div class="relative">
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-muted pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search conversations…"
              class="w-full pl-9 pr-3 py-2 bg-theme-bg2 border border-theme-border text-theme-text rounded-xl text-sm focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:outline-none placeholder-theme-muted transition"
            />
          </div>
        </div>

        <!-- Conversation list -->
        <div class="flex-1 overflow-y-auto overscroll-contain">
          <!-- Loading skeleton -->
          <div v-if="isLoading" class="space-y-0">
            <div v-for="i in 6" :key="i" class="flex items-center gap-3 px-4 py-3.5 animate-pulse">
              <div class="w-12 h-12 rounded-full bg-theme-bg2 shrink-0"></div>
              <div class="flex-1 space-y-2">
                <div class="h-3.5 bg-theme-bg2 rounded w-3/4"></div>
                <div class="h-3 bg-theme-bg2 rounded w-1/2"></div>
              </div>
            </div>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="flex flex-col items-center py-16 px-6 text-center">
            <svg class="w-10 h-10 text-red-400 mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9l6 6"/></svg>
            <p class="text-sm text-red-500 mb-4">{{ error }}</p>
            <button @click="loadChats" class="px-5 py-2 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition">
              Try Again
            </button>
          </div>

          <!-- Empty state -->
          <div v-else-if="!filteredChats.length" class="flex flex-col items-center py-20 px-6 text-center">
            <div class="w-16 h-16 rounded-2xl bg-theme-bg2 flex items-center justify-center mb-4">
              <svg class="w-8 h-8 text-theme-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </div>
            <p class="text-sm font-medium text-theme-text mb-1">No conversations yet</p>
            <p class="text-xs text-theme-muted">Start chatting from an equipment listing</p>
          </div>

          <!-- Chat items -->
          <div v-else>
            <div
              v-for="chat in filteredChats"
              :key="chat.id"
              @click="openChatView(chat.id)"
              class="chat-item flex items-center gap-3 px-4 py-3 cursor-pointer transition-all"
              :class="{
                'chat-item-active': chat.id === activeChat,
                'chat-item-unread': getUnreadCount(chat.id) > 0 && chat.id !== activeChat,
              }"
            >
              <!-- Avatar -->
              <div class="relative shrink-0">
                <img
                  v-if="getParticipantImage(chat)"
                  :src="getParticipantImage(chat)"
                  :alt="chat.otherParticipant?.first_name || 'User'"
                  class="w-12 h-12 rounded-full object-cover ring-2 ring-theme-border"
                />
                <div
                  v-else
                  class="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold text-sm ring-2 ring-theme-border"
                >
                  {{ getParticipantInitial(chat) }}
                </div>
                <!-- Unread badge -->
                <span
                  v-if="getUnreadCount(chat.id) > 0"
                  class="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 bg-primary-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-theme-card shadow-sm"
                >
                  {{ getUnreadCount(chat.id) > 9 ? '9+' : getUnreadCount(chat.id) }}
                </span>
                <!-- Online indicator -->
                <span
                  v-else-if="chat.otherParticipant?.is_online"
                  class="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-500 ring-2 ring-theme-card"
                />
              </div>

              <!-- Info -->
              <div class="flex-1 min-w-0">
                <div class="flex justify-between items-center gap-2 mb-0.5">
                  <h3 class="text-[13px] font-semibold text-theme-text truncate">
                    {{ chat.equipment?.name || chat.item_name || 'Chat' }}
                  </h3>
                  <span
                    class="text-[10px] shrink-0"
                    :class="getUnreadCount(chat.id) > 0 ? 'text-primary-600 font-semibold' : 'text-theme-muted'"
                  >
                    {{ formatRelativeTime(chat.lastMessageTime) }}
                  </span>
                </div>
                <p class="text-xs text-theme-muted truncate leading-relaxed">
                  <span class="font-medium">@{{ getParticipantDisplayName(chat) }}</span>
                  <span v-if="chat.lastMessage"> · </span>
                  {{ chat.lastMessage || 'No messages yet' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <!-- ═══════════ CHAT WINDOW ═══════════ -->
      <div v-if="activeChat" class="flex-1 flex flex-col min-w-0 min-h-0 bg-theme-bg2">

        <!-- Chat header -->
        <header class="chat-header flex items-center gap-3 px-4 py-2.5 border-b border-theme-border bg-theme-card shrink-0">
          <!-- Back button (mobile) -->
          <button
            @click="goBack"
            class="md:hidden w-9 h-9 flex items-center justify-center rounded-xl hover:bg-theme-bg2 text-theme-muted transition shrink-0"
          >
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m15 18-6-6 6-6"/></svg>
          </button>

          <!-- Avatar -->
          <img
            v-if="activeChatParticipantImage"
            :src="activeChatParticipantImage"
            class="w-10 h-10 rounded-full object-cover ring-2 ring-theme-border shrink-0"
          />
          <div
            v-else
            class="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold text-sm ring-2 ring-theme-border shrink-0"
          >
            {{ activeChatInitial }}
          </div>

          <!-- Name & items -->
          <div class="flex-1 min-w-0">
            <h2 class="text-sm font-bold text-theme-text truncate leading-tight">{{ activeChatEquipmentName }}</h2>
            <div class="flex items-center gap-1.5">
              <p class="text-[11px] text-theme-muted truncate">@{{ activeChatDisplayName }}</p>
              <span v-if="activeChatOnlineStatus" class="flex items-center gap-1 shrink-0">
                <span class="text-theme-muted text-[11px]">·</span>
                <span
                  class="w-1.5 h-1.5 rounded-full shrink-0"
                  :class="activeChatOnlineStatus.online ? 'bg-green-500' : 'bg-theme-muted'"
                />
                <span
                  class="text-[11px] truncate"
                  :class="activeChatOnlineStatus.online ? 'text-green-500' : 'text-theme-muted'"
                >{{ activeChatOnlineStatus.label }}</span>
              </span>
            </div>
          </div>

          <!-- Item image thumbnail (top-right) -->
          <button
            v-if="activeChatItemImage"
            @click="openImagePreview(activeChatItemImage)"
            class="shrink-0 w-10 h-10 rounded-xl overflow-hidden ring-2 ring-theme-border hover:ring-primary-500 transition"
            title="View item"
          >
            <img :src="activeChatItemImage" alt="Item" class="w-full h-full object-cover" />
          </button>
        </header>

        <!-- Messages -->
        <div
          ref="messagesContainer"
          class="flex-1 overflow-y-auto overscroll-contain px-3 sm:px-5 py-4 min-h-0"
        >
          <!-- Empty -->
          <div v-if="!chatMessages.length" class="flex flex-col items-center justify-center py-12">
            <div class="w-16 h-16 rounded-2xl bg-theme-card border border-theme-border flex items-center justify-center mb-3">
              <svg class="w-7 h-7 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </div>
            <p class="text-sm font-medium text-theme-text mb-0.5">Start the conversation</p>
            <p class="text-xs text-theme-muted">Send a message to get things going</p>
          </div>

          <!-- Messages list -->
          <template v-else>
            <!-- Load older messages button -->
            <div v-if="hasOlderMessages" class="flex justify-center mb-4">
              <button
                @click="loadOlderMessages"
                class="px-4 py-1.5 text-xs font-semibold text-primary-600 bg-theme-card border border-theme-border rounded-full hover:bg-theme-bg2 transition shadow-sm"
              >
                Load older messages
              </button>
            </div>

            <template v-for="(message, idx) in displayedMessages" :key="message.id">
              <!-- Date separator -->
              <div v-if="shouldShowDate(displayedMessages, idx)" class="flex items-center gap-3 my-4">
                <div class="flex-1 h-px bg-theme-border"></div>
                <span class="text-[11px] text-theme-muted font-medium px-1 shrink-0">
                  {{ formatDateLabel(message.sent_at) }}
                </span>
                <div class="flex-1 h-px bg-theme-border"></div>
              </div>

              <!-- Bubble -->
              <div
                class="flex mb-1.5"
                :class="[
                  message.sentBy === 'me' ? 'justify-end' : 'justify-start',
                  shouldAddSpacing(displayedMessages, idx) ? 'mt-3' : ''
                ]"
              >
                <div
                  class="msg-bubble relative max-w-[80%] sm:max-w-[65%] px-3.5 py-2 text-[13.5px] leading-relaxed"
                  :class="message.sentBy === 'me'
                    ? 'msg-mine bg-transparent border border-primary-500 text-theme-text'
                    : 'msg-theirs bg-theme-card text-theme-text border border-theme-border'"
                >
                  <!-- Item image (from backend) -->
                  <img
                    v-if="message.imageUrl"
                    :src="message.imageUrl + '?t=' + Date.now()"
                    alt="Item"
                    class="rounded-lg mb-1.5 max-h-40 w-auto object-cover cursor-pointer"
                    :class="message.sentBy === 'me' ? 'ring-1 ring-primary-500/40' : 'ring-1 ring-theme-border'"
                    @click="openImagePreview(message.imageUrl)"
                  />
                  <p class="break-words whitespace-pre-wrap" v-html="formatMessageHtml(message.text, message.sentBy)"></p>
                  <div
                    class="flex items-center justify-end gap-1 mt-0.5 -mb-0.5"
                    :class="message.sentBy === 'me' ? 'text-primary-500/70' : 'text-theme-muted'"
                  >
                    <span class="text-[10px]">{{ formatTime(message.sent_at) }}</span>
                    <!-- Read receipts -->
                    <svg
                      v-if="message.sentBy === 'me'"
                      class="w-3.5 h-3.5"
                      :class="message.read ? 'text-primary-500' : 'text-primary-500/40'"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                    >
                      <template v-if="message.read">
                        <path d="m2 13 4 4L15 8" /><path d="m8 13 4 4L21 8" />
                      </template>
                      <path v-else d="m5 13 4 4L18 8" />
                    </svg>
                  </div>
                </div>
              </div>
            </template>
          </template>
        </div>

        <!-- Input bar -->
        <div class="shrink-0 px-3 sm:px-4 py-2.5 border-t border-theme-border bg-theme-card relative">
          <div class="flex items-end gap-2 max-w-3xl mx-auto">
            <textarea
              ref="messageInputRef"
              v-model="newMessage"
              @keydown="handleKeyDown"
              placeholder="Type message…"
              rows="1"
              class="msg-input flex-1 resize-none px-4 py-2.5 bg-theme-bg2 border border-theme-border text-theme-text rounded-2xl text-sm focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:outline-none placeholder-theme-muted transition leading-relaxed"
            ></textarea>
            <button
              @click="handleSendMessage"
              :disabled="!newMessage.trim()"
              class="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-600 text-white hover:bg-primary-700 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed transition-all shrink-0 mb-px"
            >
              <svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
            </button>
          </div>
        </div>
        <!-- Spacer to lift input area up -->
        <div class="shrink-0 h-[100px] bg-theme-bg2"></div>
      </div>

      <!-- ═══════════ EMPTY STATE (desktop, no chat selected) ═══════════ -->
      <div v-else class="hidden md:flex flex-1 flex-col items-center justify-center bg-theme-bg2">
        <div class="text-center max-w-xs">
          <div class="w-24 h-24 mx-auto rounded-3xl bg-gradient-to-br from-primary-500/10 to-primary-600/5 border border-primary-500/10 flex items-center justify-center mb-5">
            <svg class="w-10 h-10 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          </div>
          <h3 class="text-xl font-bold text-theme-text mb-2">Your Messages</h3>
          <p class="text-sm text-theme-muted leading-relaxed">
            Select a conversation from the sidebar to start chatting, or begin a new conversation from an equipment listing.
          </p>
        </div>
      </div>

    </div>

    <!-- ═══════════ IMAGE PREVIEW MODAL ═══════════ -->
    <div
      v-if="imagePreview"
      class="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      @click.self="closeImagePreview"
    >
      <button
        @click="closeImagePreview"
        class="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition"
      >
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
      </button>
      <img :src="imagePreview" alt="Preview" class="max-w-full max-h-[85vh] rounded-lg object-contain shadow-2xl" />
    </div>
  </div>
</template>


<script setup>
import {
  ref,
  onMounted,
  nextTick,
  computed,
  watch,
  onBeforeUnmount,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { useChatStore } from '@/store/chat';
import { resolveImageUrl } from '@/composables/useImageUrl';
import { useLogger } from '@/composables/useLogger';

const logger = useLogger('Chats');

const route = useRoute();

const authStore = useAuthStore();
const chatStore = useChatStore();

const { chats, activeChat, newMessage, totalUnread } = storeToRefs(chatStore);
const { messages } = chatStore;

const {
  fetchChats,
  fetchMessages,
  getUnreadCount,
  startChatsPolling,
  stopChatsPolling,
  startActiveChatPolling,
  stopActiveChatPolling,
  _updateTitle,
} = chatStore;

// ── Local UI state ─────────────────────────────────────────────────────────
const messagesContainer = ref(null);
const messageInputRef = ref(null);
const isLoading = ref(false);
const error = ref(null);
const searchQuery = ref('');

// ── Computed ───────────────────────────────────────────────────────────────
const filteredChats = computed(() => {
  if (!searchQuery.value.trim()) return chats.value;
  const q = searchQuery.value.toLowerCase();
  return chats.value.filter(
    (chat) =>
      chat.item_name?.toLowerCase().includes(q) ||
      chat.name?.toLowerCase().includes(q) ||
      chat.otherParticipant?.first_name?.toLowerCase().includes(q) ||
      chat.otherParticipant?.last_name?.toLowerCase().includes(q)
  );
});

const chatMessages = computed(() => {
  const chatId = activeChat.value;
  if (!chatId) return [];
  return messages[chatId] || [];
});

// ── Message pagination (show latest N, load-more for older) ────────────────
const INITIAL_MESSAGES = 8;
const LOAD_MORE_COUNT = 20;
const visibleCount = ref(INITIAL_MESSAGES);

const displayedMessages = computed(() => {
  const all = chatMessages.value;
  if (all.length <= visibleCount.value) return all;
  return all.slice(all.length - visibleCount.value);
});

const hasOlderMessages = computed(() => chatMessages.value.length > visibleCount.value);

const loadOlderMessages = () => {
  // Remember current scroll height to maintain position
  const container = messagesContainer.value;
  const prevHeight = container?.scrollHeight || 0;
  visibleCount.value += LOAD_MORE_COUNT;
  nextTick(() => {
    if (container) {
      container.scrollTop = container.scrollHeight - prevHeight;
    }
  });
};

// Active chat derived data
const activeChatData = computed(() =>
  chats.value.find((c) => c.id === activeChat.value) || null
);

const activeChatParticipantImage = computed(() => {
  const p = activeChatData.value?.otherParticipant;
  return p?.image_url ? resolveImageUrl(p.image_url) : null;
});

const activeChatInitial = computed(() => {
  const p = activeChatData.value?.otherParticipant;
  return (p?.first_name?.[0] || p?.username?.[0] || '?').toUpperCase();
});

const activeChatDisplayName = computed(() => {
  const p = activeChatData.value?.otherParticipant;
  return p?.first_name || p?.username || 'Unknown';
});

const activeChatEquipmentName = computed(() => {
  return activeChatData.value?.equipment?.name || activeChatData.value?.item_name || route.query.itemName || 'Chat';
});

const activeChatItemImage = computed(() => {
  const eq = activeChatData.value?.equipment;
  const url = eq?.image_url || activeChatData.value?.itemImage || null;
  // also fall back to the URL passed in the query param (instant display on first load)
  return resolveImageUrl(url) || route.query.itemImg || null;
});

// ── Participant helpers ────────────────────────────────────────────────────
const getParticipantImage = (chat) => {
  const p = chat?.otherParticipant;
  return p?.image_url ? resolveImageUrl(p.image_url) : null;
};

const getParticipantInitial = (chat) => {
  const p = chat?.otherParticipant;
  return (p?.first_name?.[0] || p?.username?.[0] || '?').toUpperCase();
};

const getParticipantDisplayName = (chat) => {
  const p = chat?.otherParticipant;
  return p?.first_name || p?.username || 'Unknown';
};

const formatLastSeen = (lastSeenIso) => {
  if (!lastSeenIso) return 'Offline';
  const diff = Math.floor((Date.now() - new Date(lastSeenIso)) / 1000);
  if (diff < 60) return 'Last seen just now';
  if (diff < 3600) return `Last seen ${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `Last seen ${Math.floor(diff / 3600)}h ago`;
  return `Last seen ${Math.floor(diff / 86400)}d ago`;
};

const activeChatOnlineStatus = computed(() => {
  const p = activeChatData.value?.otherParticipant;
  if (!p) return null;
  if (p.is_online) return { online: true, label: 'Online' };
  return { online: false, label: formatLastSeen(p.last_seen) };
});

// ── Actions ────────────────────────────────────────────────────────────────
const handleSendMessage = async () => {
  if (!newMessage.value.trim()) return;
  await chatStore.sendMessage();
  await nextTick();
  scrollToBottom();
  autoResizeInput();
};

const handleKeyDown = (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    handleSendMessage();
  }
};

// ── Plain message formatting (no @mention highlighting needed now) ──────────────────────

const escapeHtml = (str) =>
  str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const formatMessageHtml = (text) => {
  if (!text) return '';
  return escapeHtml(text);
};

const goBack = () => {
  activeChat.value = null;
};

const imagePreview = ref(null);
const openImagePreview = (url) => {
  imagePreview.value = url;
};
const closeImagePreview = () => {
  imagePreview.value = null;
};

const loadChats = async () => {
  if (!authStore.user) {
    error.value = 'Please log in to view chats.';
    return;
  }
  isLoading.value = true;
  error.value = null;
  try {
    await fetchChats();
  } catch (err) {
    logger.error('Error loading chats', err);
    error.value = err?.response?.data?.detail || 'Failed to load chats. Please try again later.';
  } finally {
    isLoading.value = false;
  }
};

const openChatView = async (chatId) => {
  try {
    visibleCount.value = INITIAL_MESSAGES;
    await fetchMessages(chatId);
    // Reset title unread count when user opens a chat
    _updateTitle(totalUnread.value);
    await nextTick();
    scrollToBottom();
    messageInputRef.value?.focus();
  } catch (err) {
    logger.error('Error opening chat', err);
  }
};

const scrollToBottom = () => {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

// Auto-resize textarea
const autoResizeInput = () => {
  const el = messageInputRef.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 120) + 'px';
};

// ── Formatters ─────────────────────────────────────────────────────────────
const formatTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHrs < 24) return `${diffHrs}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const formatDateLabel = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
};

const shouldShowDate = (msgs, index) => {
  if (index === 0) return true;
  return new Date(msgs[index].sent_at).toDateString() !== new Date(msgs[index - 1].sent_at).toDateString();
};

const shouldAddSpacing = (msgs, index) => {
  if (index === 0) return false;
  return msgs[index].sentBy !== msgs[index - 1].sentBy;
};

// ── Auto-resize on input ───────────────────────────────────────────────────
watch(newMessage, () => nextTick(autoResizeInput));

// ── Poll active chat ───────────────────────────────────────────────────────
watch(
  () => activeChat.value,
  (chatId) => {
    stopActiveChatPolling();
    lastMsgCount = 0; // reset so new-message scroll works correctly on chat switch
    if (chatId) startActiveChatPolling(1500);
  }
);

// Auto-scroll only when new messages arrive (length increases)
let lastMsgCount = 0;
watch(chatMessages, async (msgs) => {
  if (msgs.length > lastMsgCount) {
    await nextTick();
    scrollToBottom();
  }
  lastMsgCount = msgs.length;
}, { deep: true });

// ── Reload chats list whenever this tab becomes the active route ──────────
watch(
  () => route.name,
  async (name) => {
    if (name === 'Chats') {
      await loadChats();
      // Resume polling for already-open chat
      if (activeChat.value) {
        fetchMessages(activeChat.value, { silent: true });
        startActiveChatPolling(1500);
      }
    }
  }
);

// ── Lifecycle ──────────────────────────────────────────────────────────────
onMounted(async () => {
  // Chats list is already polled globally by Navbar — just do a fresh load here
  await loadChats();

  // If a chat was already open (navigated back), re-fetch its messages and restart polling
  if (activeChat.value && !route.query.open) {
    fetchMessages(activeChat.value, { silent: true });
    startActiveChatPolling(1500);
    await nextTick();
    scrollToBottom();
  }

  // Auto-open a specific chat if navigated here with ?open=<chatId>
  const openId = route.query.open;
  if (openId) {
    await openChatView(openId);
    // Pre-fill draft message if provided
    if (route.query.draft) {
      newMessage.value = route.query.draft;
      await nextTick();
      autoResizeInput();
    }
  }
});

onBeforeUnmount(() => {
  // Don't stop chats polling here — Navbar manages it globally
  stopActiveChatPolling();
});
</script>


<style scoped>
/* ── Layout ─────────────────────────────────────────── */
.sidebar {
  width: 100%;
  max-width: 100%;
}
@media (min-width: 768px) {
  .sidebar {
    width: 340px;
    max-width: 340px;
  }
  .sidebar-hidden {
    display: flex;
  }
}
@media (max-width: 767px) {
  .sidebar-hidden {
    display: none;
  }
}

/* ── Chat items ─────────────────────────────────────── */
.chat-item {
  border-left: 3px solid transparent;
  transition: all 0.15s ease;
}
.chat-item:hover {
  background: var(--color-bgTertiary);
}
.chat-item-active {
  background: rgba(230, 141, 0, 0.1);
  border-left-color: var(--color-primary-600);
}
.chat-item-unread {
  background: rgba(230, 141, 0, 0.06);
}

/* ── Message bubbles ────────────────────────────────── */
.msg-bubble {
  border-radius: 18px;
}
.msg-mine {
  border-bottom-right-radius: 6px;
}
.msg-theirs {
  border-bottom-left-radius: 6px;
}

/* ── @mention highlights (inside v-html) ────────────── */
.msg-bubble :deep(.mention-tag) {
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 0.93em;
  white-space: nowrap;
}
.msg-mine :deep(.mention-mine) {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}
.msg-theirs :deep(.mention-theirs) {
  background: rgba(230, 141, 0, 0.12);
  color: var(--color-primary-600);
}

/* ── Textarea auto-height ───────────────────────────── */
.msg-input {
  max-height: 120px;
  scrollbar-width: thin;
  line-height: 1.45;
}

/* ── Scrollbar ──────────────────────────────────────── */
.chat-page ::-webkit-scrollbar {
  width: 5px;
}
.chat-page ::-webkit-scrollbar-track {
  background: transparent;
}
.chat-page ::-webkit-scrollbar-thumb {
  background: var(--color-border);
  border-radius: 10px;
}
.chat-page ::-webkit-scrollbar-thumb:hover {
  background: var(--color-textMuted);
}
</style>
